import { onBeforeUnmount, ref, watch, type ComputedRef, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { ClassicKeyTravel, KeyTravelList, KeyTravelWriteList } from '@rapoo/device-sdk'
import { KeyTravelFamily } from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores/device'
import { defaultReportError } from '@/composables/useErrorLogger'
import { createCoalesceLatestRequestQueue } from '@/utils/coalesceLatestRequest'

export type RTIndex = 0 | 1 | 2 | 3

export type PresetTravelValues = {
  rtTriggerSensitivity: number
  rtResetSensitivity: number
  bottomDeadZone: number
}

// 跨页面实例保留去抖任务并串行化写入，使新页面初始化能够冲刷、等待旧页面的任务。
let itineraryCommitQueue: Promise<void> = Promise.resolve()
let itineraryCommitTimer: ReturnType<typeof setTimeout> | undefined
let pendingItineraryCommit: (() => Promise<void>) | undefined

const enqueueItineraryCommit = (task: () => Promise<void>) => {
  itineraryCommitQueue = itineraryCommitQueue.then(task, task)
  return itineraryCommitQueue
}

const scheduleItineraryCommit = (task: () => Promise<void>) => {
  pendingItineraryCommit = task
  if (itineraryCommitTimer) clearTimeout(itineraryCommitTimer)
  itineraryCommitTimer = setTimeout(() => {
    itineraryCommitTimer = undefined
    const pending = pendingItineraryCommit
    pendingItineraryCommit = undefined
    if (pending) void enqueueItineraryCommit(pending)
  }, 300)
}

async function flushItineraryCommits() {
  if (itineraryCommitTimer) {
    clearTimeout(itineraryCommitTimer)
    itineraryCommitTimer = undefined
  }

  const pending = pendingItineraryCommit
  pendingItineraryCommit = undefined
  if (pending) await enqueueItineraryCommit(pending)
  else await itineraryCommitQueue
}

/**
 * SDK 层所需的业务状态接口。
 * 由外层初始化后传入，保证单向数据流，避免模块级单例耦合。
 */
export interface ItineraryDeviceState {
  rtSwitchIndex: Ref<number>
  presetValues: PresetTravelValues[]
  editingState: ClassicKeyTravel
  selectedKeys: Ref<{ index: number }[]>
  isPresetMode: ComputedRef<boolean>
  isCustomMode: ComputedRef<boolean>
  getKeyTravel: (index: number) => ClassicKeyTravel
  setPresetValues: (index: 0 | 1 | 2, values: PresetTravelValues) => void
  applyPresetToAllKeys: (preset: PresetTravelValues) => void
  populateCustomKeys: (list: KeyTravelList) => void
  withSyncing: (fn: () => void) => void
}

/**
 * 行程功能的设备 I/O（合并原 useItinerarySet + useItinerarySDK）。
 *
 * - 读取（初始化/切换/上报）只把设备数据回填到状态，永远不会写回设备；
 * - 写入只在用户显式编辑时由组件调用 commitTravel 触发；
 * - 无模块级单例：状态与加载令牌均为实例作用域，需在页面最外层调用一次。
 */
export function useItineraryDevice(state: ItineraryDeviceState) {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const isInitializing = ref(false)
  let loadToken = 0

  /** 上报触发的行程回读：最多 1 in-flight + 1 pending */
  const rtReportFetchQueue = createCoalesceLatestRequestQueue('itinerary.rtReport')

  /* -------- 底层 SDK 封装（原 useItinerarySet） -------- */

  const getKeyTravelFeature = () => {
    const keyItinerary = currentDevice.value?.features.keyTravel
    if (!keyItinerary) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    return keyItinerary
  }

  const getCurrentRTConfig = async () => {
    const keyItinerary = getKeyTravelFeature()
    if (!keyItinerary) return

    try {
      return await keyItinerary.getGlobalRTConfig()
    } catch (error) {
      console.error('Failed to get RT config', error)
      defaultReportError(`Failed to get RT config ${JSON.stringify(error)}`)
    }
  }

  const setRTConfigByIndex = async (index: RTIndex) => {
    const keyItinerary = getKeyTravelFeature()
    if (!keyItinerary) return

    try {
      await keyItinerary.setGlobalRTConfig(index)
    } catch (error) {
      console.error('Failed to set RT config', error)
      defaultReportError(`Failed to set RT config ${JSON.stringify(error)}`)
    }
  }

  const getKeyItineraryList = async (rtIndex: RTIndex) => {
    const keyItinerary = getKeyTravelFeature()
    if (!keyItinerary) return

    try {
      return await keyItinerary.getKeyTravel(rtIndex)
    } catch (error) {
      console.error('Failed to get key itinerary list', error)
      defaultReportError(`Failed to get key itinerary list ${JSON.stringify(error)}`)
    }
  }

  const setKeyItineraryList = async (payload: KeyTravelWriteList, rtIndex?: RTIndex) => {
    const keyItinerary = getKeyTravelFeature()
    if (!keyItinerary) return

    try {
      await keyItinerary.setKeyTravel(payload, rtIndex)
    } catch (error) {
      console.error('Failed to set key itinerary list', error)
      defaultReportError(`Failed to set key itinerary list ${JSON.stringify(error)}`)
    }
  }

  /* -------- 读写编排（原 useItinerarySDK） -------- */

  /**
   * 将设备返回的行程数据回填到显示状态
   * - 预设（0/1/2）：返回长度为 1 的数组，写入 presetValues 并回显 editingState
   * - 自定义（3）：返回每个按键的行程，写入 keyTravelMap
   */
  function applyDeviceData(index: RTIndex, data: KeyTravelList) {
    const {
      setPresetValues,
      presetValues,
      applyPresetToAllKeys,
      editingState,
      populateCustomKeys,
      withSyncing,
    } = state

    withSyncing(() => {
      if (index < 3) {
        const item = data[0]
        if (!item || item.family !== 'classic') return
        const { absoluteTrigger, rtTriggerSensitivity, rtResetSensitivity, bottomDeadZone } =
          item.keyTravel
        setPresetValues(index as 0 | 1 | 2, {
          rtTriggerSensitivity,
          rtResetSensitivity,
          bottomDeadZone,
        })
        const preset = presetValues[index]
        applyPresetToAllKeys(preset)
        editingState.rtTriggerEnable = true
        editingState.rtTriggerSensitivity = preset.rtTriggerSensitivity
        editingState.rtResetSensitivity = preset.rtResetSensitivity
        editingState.bottomDeadZone = preset.bottomDeadZone
        // 预设模式同样需要回显触发行程（来自设备数据）
        editingState.absoluteTrigger = absoluteTrigger
      } else {
        populateCustomKeys(data)
      }
    })
  }

  /**
   * 从设备读取并初始化全部显示状态
   */
  async function loadFromDevice() {
    const token = ++loadToken
    isInitializing.value = true

    try {
      // 切页回来读取前，先等待上一页面尚未完成的去抖写入，避免命中写入前的旧缓存。
      await flushItineraryCommits()
      if (token !== loadToken) return

      // 1. 读取三个预设（灵敏度/死区）用于标签显示
      const presets: (KeyTravelList | undefined)[] = await Promise.all([
        getKeyItineraryList(0),
        getKeyItineraryList(1),
        getKeyItineraryList(2),
      ])
      if (token !== loadToken) return
      presets.forEach((data, i) => {
        if (data?.[0]?.family === KeyTravelFamily.Classic) {
          const { rtTriggerSensitivity, rtResetSensitivity, bottomDeadZone } = data[0].keyTravel
          state.setPresetValues(i as 0 | 1 | 2, {
            rtTriggerSensitivity,
            rtResetSensitivity,
            bottomDeadZone,
          })
        }
      })

      // 2. 读取当前 RT 索引（withSyncing 避免触发 model 里 rtSwitchIndex 的副作用 wipe）
      const currentRT = await getCurrentRTConfig()
      if (token !== loadToken || currentRT === undefined) return
      const rtIndex = currentRT as RTIndex
      state.withSyncing(() => {
        state.rtSwitchIndex.value = rtIndex
      })

      // 3. 回填当前索引的完整行程（预设复用已取数据，自定义额外读取）
      const data = rtIndex < 3 ? presets[rtIndex] : await getKeyItineraryList(3)
      if (token !== loadToken) return
      if (data) applyDeviceData(rtIndex, data)
    } catch (error) {
      console.error('Failed to initialize itinerary data from device', error)
      defaultReportError(`failed to initialize itinerary data from device ${JSON.stringify(error)}`)
    } finally {
      if (token === loadToken) isInitializing.value = false
    }
  }

  /**
   * 用户切换 RT 索引：写入设备当前索引，并读取该索引行程回填（不写入内容）。
   * 切换 RT 后设备的触发行程等参数保持不变，需要读取最新值用于回显。
   */
  async function switchRT(index: RTIndex) {
    state.withSyncing(() => {
      state.rtSwitchIndex.value = index
    })
    // 切换档位时清空选中（原 rtSwitchIndex watch 的职责，现由 withSyncing 跳过）
    state.selectedKeys.value = []
    await setRTConfigByIndex(index)
    const data = await getKeyItineraryList(index)
    if (data) applyDeviceData(index, data)
  }

  /**
   * 设备上报 RT 切换：设备已自行切换，这里只更新索引并读取行程回填（不写回设备）。
   * 短时间多次上报时合并回读，避免同地址连读 busy。
   */
  async function applyReportedSwitch(index: RTIndex) {
    state.withSyncing(() => {
      state.rtSwitchIndex.value = index
    })
    state.selectedKeys.value = []
    rtReportFetchQueue.enqueue(async () => {
      const data = await getKeyItineraryList(index)
      if (data) applyDeviceData(index, data)
    })
  }

  /**
   * 立即写入设备（不经去抖）。自定义模式串行写入，避免并发 USB 导致部分键失败。
   */
  async function commitTravelImmediate() {
    const {
      isPresetMode,
      isCustomMode,
      rtSwitchIndex,
      presetValues,
      editingState,
      selectedKeys,
      getKeyTravel,
    } = state

    if (isPresetMode.value) {
      const index = rtSwitchIndex.value as 0 | 1 | 2
      const preset = presetValues[index]
      const payload: KeyTravelWriteList = [
        {
          family: KeyTravelFamily.Classic,
          index: 0,
          keyTravel: {
            absoluteTrigger: editingState.absoluteTrigger,
            rtTriggerSensitivity: preset.rtTriggerSensitivity,
            rtResetSensitivity: preset.rtResetSensitivity,
            rtTriggerEnable: true,
            absoluteTriggerEnable: editingState.absoluteTriggerEnable,
            bottomDeadZone: preset.bottomDeadZone,
          },
        },
      ]
      await setKeyItineraryList(payload, index)
      return
    }

    if (isCustomMode.value) {
      if (!selectedKeys.value.length) return
      // 逐键串行写入，降低并发写丢失（WASD/A-Z 大批量时尤甚）
      for (const { index } of selectedKeys.value) {
        await setKeyItineraryList([
          {
            family: KeyTravelFamily.Classic,
            index,
            keyTravel: { ...getKeyTravel(index) },
          },
        ])
      }
    }
  }

  /**
   * 用户编辑内容 → 去抖写入设备（按当前模式写预设槽或选中按键）。
   * 由各编辑组件在修改 editingState 后显式调用。
   */
  const commitTravel = () => {
    scheduleItineraryCommit(commitTravelImmediate)
  }

  // 切页卸载前冲刷未写出的去抖写入；新页面 loadFromDevice 会等待同一写入队列。
  onBeforeUnmount(() => {
    void flushItineraryCommits()
  })

  watch(
    () => currentDevice.value,
    (device) => {
      if (device) loadFromDevice()
    },
    { immediate: true },
  )

  return {
    isInitializing,
    reload: loadFromDevice,
    switchRT,
    applyReportedSwitch,
    commitTravel,
  }
}

export type UseItineraryDeviceReturn = ReturnType<typeof useItineraryDevice>
